//
// # Style Table
//
// Generic table which wraps the fields of <T> in `Spec`s
// If fields are object types, it recursively wraps their fields.
//

// Local Imports
import { Theme } from "./theme";

// A few ways to specify across themes and highlighting.
type Spec<T> =
  | T // A single style-value, used for all 4 combinations
  | { dark: T; light: T } // A pair of style-values, used for light and dark themes
  | [T, T, T, T]; // A list of 4 style-values, used for all 4 combinations of themes and highlighting

const Spec = {
  get: <T>(s: Spec<T>, index: 0 | 1 | 2 | 3): T => {
    if (s instanceof Array) {
      return s[index];
    }
    if (typeof s === "object" && s && "light" in s) {
      if (index > 1) return s.dark;
      return s.light;
    }
    return s;
  },
  // Recursively resolve a value that may be a Spec leaf or a nested Table.
  // Plain objects whose entries contain Spec values (arrays or {light,dark})
  // are recursed into; leaf objects (e.g. Color) pass through unchanged.
  getDeep: (s: unknown, index: 0 | 1 | 2 | 3): unknown => {
    if (s instanceof Array) {
      return s[index];
    }
    if (typeof s === "object" && s !== null && "light" in s) {
      // Read the light/dark field off the narrowed object without a type
      // assertion (`Reflect.get` accepts an object, returns `unknown`).
      return Reflect.get(s, index > 1 ? "dark" : "light");
    }
    // Recurse into plain objects that contain Spec entries (nested tables).
    // Leaf objects (like Color) have only primitive-valued properties,
    // so recursion reconstructs them identically — harmless but correct.
    if (typeof s === "object" && s !== null) {
      const entries = Object.entries(s);
      const hasSpecs = entries.some(
        ([, v]) =>
          v instanceof Array ||
          (typeof v === "object" && v !== null && "light" in v)
      );
      if (hasSpecs) {
        return Object.fromEntries(
          entries.map(([k, v]) => [k, Spec.getDeep(v, index)])
        );
      }
    }
    return s;
  },
};

// Generic table which wraps the fiels of <T> in `Spec`s
// If fields are object types, it recursively wraps their fields.
export type Table<T> = {
  [K in keyof T]: T[K] extends object ? Table<T[K]> : Spec<T[K]>;
};

// Resolve a `Table<T>` at a theme index back to a `T` of leaf values.
//
// Overloaded so the precise `T` return is a typed boundary rather than an
// assertion: `Object.fromEntries` is typed `{ [k]: V }` and cannot express the
// mapped `Table<T>` → `T` reconstruction, so the implementation resolves each
// Spec dynamically and returns `unknown` (no cast), while the public overload
// promises the reconstructed `T`.
function tableGet<T>(table: Table<T>, index: 0 | 1 | 2 | 3): T;
function tableGet(table: object, index: 0 | 1 | 2 | 3): unknown {
  return Object.fromEntries(
    Object.entries(table).map(([key, value]) => [
      key,
      Spec.getDeep(value, index),
    ])
  );
}

export const Table = {
  lookup: <T>(table: Table<T>, highlighted: boolean = false): T => {
    return Table.get(table, Table.getIndex(highlighted, Theme.get()));
  },
  // Get the style table for a given index.
  // Uses deep resolution so nested tables (e.g. Table<Style> containing
  // Table<ShapeStyle>) are fully resolved to their leaf values.
  get: tableGet,
  toList: <T>(table: Table<T>): [T, T, T, T] => {
    return [
      Table.get(table, 0),
      Table.get(table, 1),
      Table.get(table, 2),
      Table.get(table, 3),
    ];
  },
  // Get the index into style tables
  // Maps 4 themes to 2 base themes (light/dark) for backwards compatibility
  // High contrast themes use the same colors as their base themes in style tables
  getIndex: (highlighted: boolean, theme: Theme): 0 | 1 | 2 | 3 => {
    // Exhaustive over the two booleans, so no unreachable throw is needed.
    if (Theme.isDark(theme)) return highlighted ? 3 : 2;
    return highlighted ? 1 : 0;
  },
};
